import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { safeValidateAddress } from '@mls-wallet/shared-types';
import { useEditAddressBook } from '@/hooks/organization/use-edit-address-book';
import { useAutoLoadMore } from '@/hooks/use-auto-load-more';
import { useAddressBookEntryByAddress } from '@/hooks/organization/use-address-book-entry-by-address';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { formatAddress } from '@/lib/utils';
import type { AddressBookEntryType } from '@/types/address-book';
import { Button } from '@/components/ui/button';
import { AddressDisplay } from './address-display';
import {
  AddressSelectorDialog,
  AddressSelectorResults,
  AddressSelectorRow,
} from './address-selector';

type AddressBookSelectorOption = {
  id: string | null;
  address: string;
  type: AddressBookEntryType;
  name: string | null;
};

type AddressBookSelectorFilter =
  | 'ALL'
  | 'MLS_ACCOUNTS'
  | 'WALLETS'
  | 'CONTRACTS';

const ADDRESS_BOOK_SELECTOR_FILTERS = [
  { value: 'ALL', label: 'All Addresses' },
  { value: 'WALLETS', label: 'Wallets (EOAs)' },
  { value: 'CONTRACTS', label: 'Smart Contracts' },
  { value: 'MLS_ACCOUNTS', label: 'MLS Accounts' },
] satisfies Array<{ value: AddressBookSelectorFilter; label: string }>;

function getAddressBookEntryTypeForFilter(
  filter: AddressBookSelectorFilter
): AddressBookEntryType | undefined {
  switch (filter) {
    case 'WALLETS':
      return 'EOA';
    case 'CONTRACTS':
      return 'CONTRACT';
    case 'MLS_ACCOUNTS':
      return 'MLSACCOUNT';
    case 'ALL':
    default:
      return undefined;
  }
}

type SharedProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  allowedAddresses?: string[];
  searchPlaceholder?: string;
  typeFilter?: AddressBookEntryType;
  hideFilters?: boolean;
  singleSelectionRequiresConfirm?: boolean;
  leadingOptions?: Array<{
    id: string;
    title: string;
    description?: string;
    onSelect: () => void;
  }>;
};

type SingleModeProps = SharedProps & {
  mode: 'single';
  selectedAddress?: string | null | undefined;
  onAddressSelect: (address: string, label: string) => void;
  onAddressClear?: () => void;
};

type MultipleModeProps = SharedProps & {
  mode: 'multiple';
  selectedAddresses: string[];
  onConfirmSelection: (selection: {
    addresses: string[];
    entries: Array<{
      address: string;
      name: string | null;
      type: AddressBookEntryType | null;
    }>;
  }) => void;
};

type AddressBookSelectorDialogProps = SingleModeProps | MultipleModeProps;

function MissingAddressPanel({
  address,
  label,
  onLabelChange,
  onSubmit,
  submitLabel,
  isSubmitDisabled,
  isSaving,
  errorMessage,
  warningMessage,
  existingEntry,
}: {
  address: string;
  label: string;
  onLabelChange: (value: string) => void;
  onSubmit: () => void;
  submitLabel: string;
  isSubmitDisabled: boolean;
  isSaving: boolean;
  errorMessage: string | null;
  warningMessage: string | null;
  existingEntry?: {
    address: string;
    name: string | null;
  } | null;
}) {
  const inlineWarningEntry = warningMessage ? (existingEntry ?? null) : null;
  const showInlineExistingEntryWarning = inlineWarningEntry !== null;
  const panelTitle = warningMessage
    ? existingEntry
      ? 'This address cannot be used here'
      : `${formatAddress(address)} is not currently available`
    : `Save ${formatAddress(address)} to your address book to use it`;

  return (
    <div className="rounded-[10px] border border-solid border-zinc-200 bg-white p-6">
      <div className="space-y-1">
        {!showInlineExistingEntryWarning ? (
          <p className="font-medium text-[16px] text-zinc-900">{panelTitle}</p>
        ) : null}
        {warningMessage ? (
          <div className="mt-3 flex gap-3 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-left">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            {showInlineExistingEntryWarning ? (
              <div className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-amber-900">
                <AddressDisplay
                  address={inlineWarningEntry.address}
                  name={inlineWarningEntry.name}
                  showAddressName
                  addressNameLayout="inline"
                  addressNameClassName="text-sm"
                  className="max-w-full"
                />
                <span>{warningMessage}</span>
              </div>
            ) : (
              <p className="text-sm text-amber-900">{warningMessage}</p>
            )}
          </div>
        ) : null}
      </div>

      {!warningMessage ? (
        <div className="mt-4 space-y-3">
          <input
            type="text"
            value={label}
            onChange={(event) => onLabelChange(event.target.value)}
            placeholder="Address label"
            className="h-[48px] w-full rounded-[14px] border border-solid border-zinc-200 bg-white px-4 text-[16px] text-zinc-900 outline-none transition-colors placeholder:text-zinc-500 focus:border-zinc-400"
          />

          {errorMessage ? (
            <p className="text-sm text-red-600">{errorMessage}</p>
          ) : null}

          <Button
            type="button"
            disabled={isSubmitDisabled}
            onClick={onSubmit}
            className="h-[44px] w-full rounded-[12px]"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function AddressBookSelectorDialog(
  props: AddressBookSelectorDialogProps
) {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedAddress =
    props.mode === 'single' ? props.selectedAddress : null;
  const requiresSingleConfirm =
    props.mode === 'single' && props.singleSelectionRequiresConfirm;
  const [searchQuery, setSearchQuery] = useState('');
  const [newAddressLabel, setNewAddressLabel] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AddressBookSelectorFilter>('ALL');
  const [locallyCreatedEntries, setLocallyCreatedEntries] = useState<
    AddressBookSelectorOption[]
  >([]);
  const [draftSelectedAddress, setDraftSelectedAddress] = useState<
    string | null
  >(null);
  const [draftSelectedAddresses, setDraftSelectedAddresses] = useState<
    string[]
  >([]);
  const canConfirmSingleSelection =
    props.mode === 'single' &&
    requiresSingleConfirm &&
    (draftSelectedAddress !== null ||
      (selectedAddress !== null && Boolean(props.onAddressClear)));
  // All selector-based flows (transaction builder, policy pickers, etc.) route
  // address-book writes through this shared dialog. Callers only own selection
  // state; the dialog owns the "save missing address" mutation path so every
  // picker gets the same create/update behavior.
  const { editAddressBook, isLoading: isSavingAddress } = useEditAddressBook();
  const normalizedSearchQuery = searchQuery.trim();
  const normalizedSearchQueryLower = normalizedSearchQuery.toLowerCase();
  const effectiveTypeFilter =
    props.typeFilter ?? getAddressBookEntryTypeForFilter(filter);
  const multiSelectedAddresses =
    props.mode === 'multiple' ? props.selectedAddresses : null;

  const normalizedAllowedAddresses = useMemo(() => {
    return new Set(
      (props.allowedAddresses ?? []).flatMap((address) => {
        const validation = safeValidateAddress(address);
        return validation.isValid
          ? [validation.checksumAddress.toLowerCase()]
          : [];
      })
    );
  }, [props.allowedAddresses]);

  const {
    entries: addressBookEntries,
    pagination,
    hasMore,
    isLoadingMore,
    isLoading: isLoadingAddressBook,
    error: addressBookError,
    loadMore,
    refresh: refreshAddressBook,
  } = useListAddressBookEntries({
    enabled: props.open,
    ...(effectiveTypeFilter ? { type: effectiveTypeFilter } : {}),
    ...(normalizedSearchQuery.length > 0
      ? { query: normalizedSearchQuery }
      : {}),
    pagination: { limit: 100 },
  });

  const upsertLocallyCreatedEntry = useCallback(
    (entryToUpsert: AddressBookSelectorOption) => {
      setLocallyCreatedEntries((currentEntries) => {
        const addressKey = entryToUpsert.address.toLowerCase();
        const nextEntries = currentEntries.filter(
          (entry) => entry.address.toLowerCase() !== addressKey
        );
        nextEntries.unshift(entryToUpsert);
        return nextEntries;
      });
    },
    []
  );

  useEffect(() => {
    // Reset transient search/form state whenever the dialog fully closes.
    if (!props.open) {
      setSearchQuery('');
      setNewAddressLabel('');
      setSaveError(null);
      setFilter('ALL');
      setDraftSelectedAddress(null);
      setDraftSelectedAddresses([]);
    }
  }, [props.open]);

  useEffect(() => {
    if (!requiresSingleConfirm || !props.open) {
      return;
    }

    setDraftSelectedAddress(selectedAddress ?? null);
  }, [props.open, requiresSingleConfirm, selectedAddress]);

  useEffect(() => {
    // Keep multi-select draft state in sync with the caller-owned selection.
    if (!multiSelectedAddresses || !props.open) {
      return;
    }

    setDraftSelectedAddresses(multiSelectedAddresses);
  }, [multiSelectedAddresses, props.open]);

  const isAddressAllowed = useCallback(
    (address: string): boolean => {
      // Case: no allowed list means the selector is unrestricted.
      if (normalizedAllowedAddresses.size === 0) {
        return true;
      }

      const validation = safeValidateAddress(address);
      return (
        validation.isValid &&
        normalizedAllowedAddresses.has(validation.checksumAddress.toLowerCase())
      );
    },
    [normalizedAllowedAddresses]
  );

  const searchAddressValidation = safeValidateAddress(normalizedSearchQuery);
  const searchAddressKey = searchAddressValidation.isValid
    ? searchAddressValidation.checksumAddress.toLowerCase()
    : null;
  const shouldLookupExactAddress = props.open && Boolean(searchAddressKey);

  const { entry: exactSearchEntry, isLoading: isSearchingExactSearchEntry } =
    useAddressBookEntryByAddress(
      shouldLookupExactAddress && searchAddressValidation.isValid
        ? searchAddressValidation.checksumAddress
        : null
    );

  const availableAddresses = useMemo(() => {
    // Merge server rows with entries created locally during this dialog session.
    const availableByAddress = new Map<string, AddressBookSelectorOption>();

    const addOption = (entry: AddressBookSelectorOption) => {
      // Case: type-filtered pickers/tabs should ignore rows outside the active
      // server-side type scope, including entries created locally in-session.
      if (effectiveTypeFilter && entry.type !== effectiveTypeFilter) {
        return;
      }

      // Case: filter out entries blocked by the active policy restriction.
      if (!isAddressAllowed(entry.address)) {
        return;
      }

      // Case: preserve search filtering for locally created entries before the
      // next server refresh catches up.
      if (
        normalizedSearchQueryLower.length > 0 &&
        !(
          entry.name?.toLowerCase().includes(normalizedSearchQueryLower) ??
          false
        ) &&
        !entry.address.toLowerCase().includes(normalizedSearchQueryLower)
      ) {
        return;
      }

      const key = entry.address.toLowerCase();
      const existing = availableByAddress.get(key);

      // Case: first time seeing this address.
      if (!existing) {
        availableByAddress.set(key, entry);
        return;
      }

      // Case: prefer the richer/localer copy when duplicate addresses collide.
      availableByAddress.set(key, {
        ...existing,
        ...entry,
        name: entry.name || existing.name,
        type: entry.type,
        id: entry.id ?? existing.id,
      });
    };

    addressBookEntries.forEach((entry) => {
      addOption({
        id: entry.address,
        address: entry.address,
        type: entry.type,
        name: entry.name,
      });
    });
    locallyCreatedEntries.forEach(addOption);
    if (exactSearchEntry) {
      addOption({
        id: exactSearchEntry.address,
        address: exactSearchEntry.address,
        type: exactSearchEntry.type,
        name: exactSearchEntry.name,
      });
    }

    return [...availableByAddress.values()];
  }, [
    addressBookEntries,
    effectiveTypeFilter,
    exactSearchEntry,
    isAddressAllowed,
    locallyCreatedEntries,
    normalizedSearchQueryLower,
  ]);

  const exactAddressMatch = useMemo(() => {
    // Only try exact-address matching once the search text is a valid address.
    if (!searchAddressKey) {
      return undefined;
    }

    return availableAddresses.find(
      (entry) => entry.address.toLowerCase() === searchAddressKey
    );
  }, [availableAddresses, searchAddressKey]);

  const exactCrossTypeAddressMatch = useMemo(() => {
    if (!searchAddressKey || !shouldLookupExactAddress) {
      return undefined;
    }

    return (
      locallyCreatedEntries.find(
        (entry) => entry.address.toLowerCase() === searchAddressKey
      ) ?? exactSearchEntry
    );
  }, [
    exactSearchEntry,
    locallyCreatedEntries,
    searchAddressKey,
    shouldLookupExactAddress,
  ]);

  const incompatibleExactAddressMatch = useMemo(() => {
    if (!effectiveTypeFilter || !exactCrossTypeAddressMatch) {
      return undefined;
    }

    return exactCrossTypeAddressMatch.type === effectiveTypeFilter
      ? undefined
      : exactCrossTypeAddressMatch;
  }, [effectiveTypeFilter, exactCrossTypeAddressMatch]);

  const commitSingleSelection = useCallback(() => {
    if (props.mode !== 'single' || !requiresSingleConfirm) {
      return;
    }

    // Case: user cleared the staged selection and wants to commit "none".
    if (!draftSelectedAddress) {
      if (selectedAddress && props.onAddressClear) {
        props.onAddressClear();
        props.onOpenChange(false);
      }
      return;
    }

    const entry = availableAddresses.find(
      (candidate) =>
        candidate.address.toLowerCase() === draftSelectedAddress.toLowerCase()
    );

    props.onAddressSelect(
      draftSelectedAddress,
      entry?.name || draftSelectedAddress
    );
    props.onOpenChange(false);
  }, [
    availableAddresses,
    draftSelectedAddress,
    props,
    requiresSingleConfirm,
    selectedAddress,
  ]);

  const selectedAddressSet = useMemo(
    () =>
      new Set(
        (props.mode === 'multiple'
          ? draftSelectedAddresses
          : requiresSingleConfirm
            ? draftSelectedAddress
              ? [draftSelectedAddress]
              : []
            : selectedAddress
              ? [selectedAddress]
              : []
        ).map((address) => address.toLowerCase())
      ),
    [
      draftSelectedAddress,
      draftSelectedAddresses,
      props.mode,
      requiresSingleConfirm,
      selectedAddress,
    ]
  );

  useAutoLoadMore(
    loadMoreRef,
    props.open,
    hasMore,
    isLoadingMore,
    pagination.cursor,
    loadMore
  );

  const shouldShowIncompatibleAddressState =
    searchAddressValidation.isValid &&
    Boolean(incompatibleExactAddressMatch) &&
    !isSearchingExactSearchEntry;

  // Only show the missing-address panel once we have exhausted loaded pages.
  const shouldShowMissingAddressState =
    searchAddressValidation.isValid &&
    !exactAddressMatch &&
    !incompatibleExactAddressMatch &&
    !isLoadingAddressBook &&
    !isSearchingExactSearchEntry;
  const canSaveMissingAddress = true;
  const missingAddressKey =
    shouldShowMissingAddressState && searchAddressValidation.isValid
      ? searchAddressValidation.checksumAddress
      : null;
  const missingAddressWarning = shouldShowIncompatibleAddressState
    ? props.typeFilter === 'CONTRACT'
      ? 'This address is in the address book but cannot be used here because it is not a smart contract.'
      : 'This address is in the address book but cannot be used here.'
    : shouldShowMissingAddressState &&
        !isAddressAllowed(searchAddressValidation.checksumAddress)
      ? 'This address is not allowed by the selected policy.'
      : null;

  useEffect(() => {
    setNewAddressLabel('');
    setSaveError(null);
  }, [missingAddressKey, shouldShowIncompatibleAddressState]);

  const handleOptionSelect = (entry: AddressBookSelectorOption) => {
    // Case: single-select returns immediately and closes.
    if (props.mode === 'single') {
      if (requiresSingleConfirm) {
        const addressKey = entry.address.toLowerCase();
        setDraftSelectedAddress((currentAddress) =>
          currentAddress?.toLowerCase() === addressKey ? null : entry.address
        );
        return;
      }

      props.onAddressSelect(entry.address, entry.name || entry.address);
      props.onOpenChange(false);
      return;
    }

    // Multiple mode stages the selection until Confirm.
    const addressKey = entry.address.toLowerCase();
    setDraftSelectedAddresses((currentAddresses) =>
      selectedAddressSet.has(addressKey)
        ? currentAddresses.filter(
            (candidate) => candidate.toLowerCase() !== addressKey
          )
        : [...currentAddresses, entry.address]
    );
  };

  const handleCreateMissingAddress = async () => {
    if (
      !searchAddressValidation.isValid ||
      newAddressLabel.trim().length === 0 ||
      missingAddressWarning
    ) {
      return;
    }

    setSaveError(null);

    try {
      const response = await editAddressBook({
        instructions: [
          {
            action: 'upsert',
            address: searchAddressValidation.checksumAddress,
            name: newAddressLabel.trim(),
          },
        ],
      });

      const result = response.results.find(
        (candidate) =>
          candidate.action === 'upsert' &&
          candidate.entry.address.toLowerCase() ===
            searchAddressValidation.checksumAddress.toLowerCase()
      );

      if (!result || result.action !== 'upsert') {
        throw new Error('Saved address book entry missing from response.');
      }

      // Merge the saved response locally so the selector updates immediately.
      const createdEntry = {
        id: result.entry.address,
        address: result.entry.address,
        type: result.entry.type,
        name: result.entry.name,
      } satisfies AddressBookSelectorOption;

      // Case: the saved entry resolved to a different type than this picker
      // allows, so keep the dialog open and immediately flip into the warning
      // state that shows the saved address-book entry without the label input.
      if (props.typeFilter && createdEntry.type !== props.typeFilter) {
        upsertLocallyCreatedEntry(createdEntry);
        setNewAddressLabel('');
        void refreshAddressBook();
        return;
      }

      upsertLocallyCreatedEntry(createdEntry);

      void refreshAddressBook();

      // Case: single-select with confirm should bounce back to the list and
      // stage the new contract as the pending choice.
      if (props.mode === 'single' && requiresSingleConfirm) {
        setDraftSelectedAddress(createdEntry.address);
        setSearchQuery('');
        setNewAddressLabel('');
        return;
      }

      // Case: single-select should immediately return the saved address.
      if (props.mode === 'single') {
        props.onAddressSelect(
          createdEntry.address,
          createdEntry.name || createdEntry.address
        );
        props.onOpenChange(false);
        return;
      }

      // Case: multiple mode stages the new address and keeps the dialog open.
      setDraftSelectedAddresses((currentAddresses) =>
        currentAddresses.some(
          (candidate) =>
            candidate.toLowerCase() === createdEntry.address.toLowerCase()
        )
          ? currentAddresses
          : [...currentAddresses, createdEntry.address]
      );
      setSearchQuery('');
      setNewAddressLabel('');
    } catch (error) {
      setSaveError(
        getRequestErrorMessage(
          error,
          'Failed to save address to the address book.'
        )
      );
    }
  };

  const errorMessage =
    addressBookError instanceof Error
      ? addressBookError.message
      : addressBookError
        ? 'Failed to load recipients'
        : null;

  const footer =
    props.mode === 'multiple' ? (
      <div className="mt-4 flex min-w-0 flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-500">
          {draftSelectedAddresses.length} selected
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => props.onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() =>
              // Confirm commits the staged multi-select state back to the caller.
              props.onConfirmSelection({
                addresses: draftSelectedAddresses,
                entries: draftSelectedAddresses.map((address) => {
                  const existingEntry = availableAddresses.find(
                    (entry) =>
                      entry.address.toLowerCase() === address.toLowerCase()
                  );

                  return {
                    address,
                    name: existingEntry?.name ?? null,
                    type: existingEntry?.type ?? null,
                  };
                }),
              })
            }
          >
            Confirm
          </Button>
        </div>
      </div>
    ) : requiresSingleConfirm ? (
      <div className="mt-4 flex min-w-0 flex-wrap items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => props.onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!canConfirmSingleSelection}
          onClick={commitSingleSelection}
        >
          Confirm
        </Button>
      </div>
    ) : null;

  return (
    <AddressSelectorDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.title}
      description={props.description}
      titleClassName="text-2xl tracking-[-0.03em] text-zinc-950"
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      searchPlaceholder={props.searchPlaceholder ?? 'Search or paste address'}
      filters={props.hideFilters ? [] : ADDRESS_BOOK_SELECTOR_FILTERS}
      selectedFilter={filter}
      onSelectedFilterChange={(value) =>
        setFilter(value as AddressBookSelectorFilter)
      }
      contentClassName="sm:max-w-[720px]"
    >
      <div className="min-w-0 px-[20px] pb-[20px]">
        <div className="h-[360px] min-w-0">
          {(shouldShowMissingAddressState ||
            shouldShowIncompatibleAddressState) &&
          searchAddressValidation.isValid ? (
            <div className="h-full min-w-0">
              <MissingAddressPanel
                address={searchAddressValidation.checksumAddress}
                label={newAddressLabel}
                onLabelChange={(value) => {
                  setNewAddressLabel(value);
                  setSaveError(null);
                }}
                onSubmit={handleCreateMissingAddress}
                submitLabel="Save address"
                isSubmitDisabled={
                  newAddressLabel.trim().length === 0 ||
                  !canSaveMissingAddress ||
                  Boolean(missingAddressWarning) ||
                  isSavingAddress
                }
                isSaving={isSavingAddress}
                errorMessage={saveError}
                warningMessage={missingAddressWarning}
                existingEntry={incompatibleExactAddressMatch ?? null}
              />
            </div>
          ) : (
            <AddressSelectorResults
              isLoading={isLoadingAddressBook}
              errorMessage={errorMessage}
              emptyMessage={
                availableAddresses.length === 0 &&
                (props.leadingOptions?.length ?? 0) === 0
                  ? 'No addresses available'
                  : 'No addresses found'
              }
              hasResults={
                availableAddresses.length > 0 ||
                (props.leadingOptions?.length ?? 0) > 0
              }
              className="h-full min-w-0 overflow-y-auto"
            >
              {props.leadingOptions?.map((option) => (
                <AddressSelectorRow
                  key={option.id}
                  address=""
                  titleOverride={option.title}
                  descriptionOverride={option.description ?? null}
                  onClick={option.onSelect}
                />
              ))}
              {availableAddresses.map((entry) => (
                <AddressSelectorRow
                  key={entry.id ?? entry.address}
                  address={entry.address}
                  name={entry.name}
                  type={entry.type}
                  isSelected={selectedAddressSet.has(
                    entry.address.toLowerCase()
                  )}
                  onClick={() => handleOptionSelect(entry)}
                />
              ))}
              {hasMore || isLoadingMore ? (
                <div ref={loadMoreRef} className="h-1" />
              ) : null}
            </AddressSelectorResults>
          )}
        </div>

        {footer}
      </div>
    </AddressSelectorDialog>
  );
}
